<template>
  <div class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
      <!-- Header -->
      <div class="px-8 py-6 border-b border-gray-200 bg-primary-50">
        <h3 class="text-2xl font-bold text-gray-900">
          {{ question?.id ? 'Edit Question' : 'New Question' }}
        </h3>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto px-8 py-6 space-y-6">
        <!-- Intent -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Intent <span class="text-red-500">*</span>
          </label>
          <textarea
            v-model="formData.intent"
            rows="3"
            placeholder="What information is this question trying to gather? (e.g., 'assess frequency of depressive symptoms')"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all resize-none"
          />
        </div>

        <!-- Sample Question Text -->
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Sample Question Text <span class="text-red-500">*</span>
          </label>
          <textarea
            v-model="formData.sample_question_text"
            rows="3"
            placeholder="A generalized example of how this question might be asked"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all resize-none"
          />
        </div>

        <!-- Conditional Logic Toggle -->
        <div class="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h4 class="font-semibold text-gray-900 flex items-center">
                <FontAwesomeIcon :icon="['fas', 'code-branch']" class="mr-2 text-purple-600" />
                Conditional Logic
              </h4>
              <p class="text-xs text-gray-500 mt-1">Add branching based on responses</p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                v-model="hasConditional"
                class="sr-only peer"
              />
              <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
            </label>
          </div>

          <!-- Conditional Logic Editor -->
          <transition name="expand">
            <div v-if="hasConditional" class="space-y-4 mt-4 pt-4 border-t border-gray-200">
              <!-- Conditionals List -->
              <div
                v-for="(conditional, index) in formData.conditional_followup.conditionals"
                :key="index"
                class="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
              >
                <div class="flex items-center justify-between mb-2">
                  <span class="text-sm font-semibold text-gray-700">Conditional #{{ index + 1 }}</span>
                  <button
                    @click="removeConditional(index)"
                    class="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Remove"
                  >
                    <FontAwesomeIcon :icon="['fas', 'trash']" class="text-sm" />
                  </button>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    When should this trigger?
                  </label>
                  <textarea
                    v-model="conditional.condition_description"
                    rows="2"
                    placeholder="e.g., 'If patient answers yes' or 'If score > 5'"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all resize-none text-sm"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    What followup questions?
                  </label>
                  <textarea
                    v-model="conditional.followup_description"
                    rows="2"
                    placeholder="e.g., 'Ask about frequency and duration'"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all resize-none text-sm"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    v-model="conditional.notes"
                    rows="1"
                    placeholder="Additional context..."
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all resize-none text-sm"
                  />
                </div>
              </div>

              <!-- Add Conditional Button -->
              <button
                @click="addConditional"
                class="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-all text-gray-600 hover:text-primary-600 font-medium text-sm"
              >
                <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-2" />
                Add Another Conditional
              </button>
            </div>
          </transition>
        </div>

        <!-- Metadata -->
        <div class="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <h4 class="font-semibold text-gray-900 mb-4">Metadata</h4>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Question Type</label>
              <input
                v-model="formData.metadata.question_type"
                type="text"
                placeholder="e.g., screening, assessment"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Has Scoring</label>
              <select
                v-model="formData.metadata.has_scoring"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent transition-all"
              >
                <option :value="false">No</option>
                <option :value="true">Yes</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-8 py-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
        <button
          @click="$emit('close')"
          class="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-all font-medium"
        >
          Cancel
        </button>
        <button
          @click="save"
          :disabled="!isValid"
          class="btn-primary"
        >
          <FontAwesomeIcon :icon="['fas', 'check']" />
          Save Question
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'QuestionEditor',

  components: {
    FontAwesomeIcon
  },

  props: {
    question: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      formData: {
        intent: '',
        sample_question_text: '',
        conditional_followup: {},
        metadata: {
          question_type: '',
          has_scoring: false
        }
      },
      hasConditional: false
    };
  },

  computed: {
    isValid() {
      return this.formData.intent?.trim() && this.formData.sample_question_text?.trim();
    }
  },

  mounted() {
    if (this.question) {
      this.formData = {
        ...this.question,
        metadata: this.question.metadata || { question_type: '', has_scoring: false }
      };
      this.hasConditional = this.question.conditional_followup?.has_conditional || false;

      // Ensure conditionals array exists if has_conditional is true
      if (this.hasConditional && !this.formData.conditional_followup?.conditionals) {
        this.formData.conditional_followup = {
          has_conditional: true,
          conditionals: [{
            condition_description: '',
            followup_description: '',
            notes: ''
          }]
        };
      }
    }
  },

  watch: {
    hasConditional(val) {
      if (!val) {
        this.formData.conditional_followup = {};
      } else if (!this.formData.conditional_followup?.has_conditional) {
        this.formData.conditional_followup = {
          has_conditional: true,
          conditionals: [{
            condition_description: '',
            followup_description: '',
            notes: ''
          }]
        };
      }
    }
  },

  methods: {
    addConditional() {
      if (!this.formData.conditional_followup.conditionals) {
        this.formData.conditional_followup.conditionals = [];
      }
      this.formData.conditional_followup.conditionals.push({
        condition_description: '',
        followup_description: '',
        notes: ''
      });
    },

    removeConditional(index) {
      this.formData.conditional_followup.conditionals.splice(index, 1);
      if (this.formData.conditional_followup.conditionals.length === 0) {
        this.hasConditional = false;
      }
    },

    save() {
      if (!this.isValid) return;

      const payload = {
        ...this.formData,
        conditional_followup: this.hasConditional ? this.formData.conditional_followup : {}
      };

      this.$emit('save', payload);
    }
  }
};
</script>

<style scoped>
.expand-enter-active, .expand-leave-active {
  transition: all 0.3s ease;
}
.expand-enter-from, .expand-leave-to {
  opacity: 0;
  max-height: 0;
}
.expand-enter-to, .expand-leave-from {
  opacity: 1;
  max-height: 500px;
}
</style>
